/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminCommunityList from 'src/components/Admin/AdminCommunity/AdminCommunityList';
import AdminTablePlaceholder from 'src/components/Admin/AdminTablePlaceholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getFirstPageRetryVariables from './helpers/getFirstPageRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_community.admin_community_list.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading communities. Please try again.',
  },
});

export const adminCommunityListRouteQuery = graphql`
  query AdminCommunityListRoute_Query($count: Int, $cursor: String) {
    viewer {
      ...AdminCommunityList_viewer @arguments(count: $count, cursor: $cursor)
    }
  }
`;

/**
 * Renders `AdminCommunityList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin community list route content.
 */
function AdminCommunityListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminCommunityListRouteQuery, queryRef);

  return <AdminCommunityList {...routeProps} {...queryData} />;
}

AdminCommunityListPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminCommunityList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin community list route content.
 */
function AdminCommunityListRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminCommunityListRouteQuery,
    routeProps,
    { logLabel: 'AdminCommunityListRoute', getRetryVariablesFunc: getFirstPageRetryVariables },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={AdminTablePlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminCommunityListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminCommunityList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminCommunityListRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the AdminCommunityList route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. Authentication is resolved by the
 * owning `AdminLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} AdminCommunityList route content or loading state.
 */
export function adminCommunityListRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <AdminCommunityListRouteRenderer
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return <AdminCommunityListRouteRenderer routeProps={props} />;
}

export default AdminCommunityListRouteRenderer;
